/**
 * Operator API Constants
 * Configuration and constant values for the operator API client
 */

import type { OperatorRole, RolePermissions } from './types';

// =============================================================================
// Environment Configuration
// =============================================================================

/** Base API URL - uses relative URLs by default for port flexibility */
export const API_URL = import.meta.env.VITE_API_URL || '';

// =============================================================================
// Role Permissions
// =============================================================================

/** Role-based permissions for RBAC enforcement in UI */
export const ROLE_PERMISSIONS: Record<OperatorRole, RolePermissions> = {
  VIEWER: {
    canViewMetrics: true,
    canViewConversations: false,
    canViewFullPII: false,
    canSearchCustomers: false,
    canViewAuditLogs: false,
    canExportData: false,
    canManageOperators: false,
    canProcessLGPD: false,
  },
  SUPPORT: {
    canViewMetrics: true,
    canViewConversations: true,
    canViewFullPII: false,
    canSearchCustomers: true,
    canViewAuditLogs: false,
    canExportData: false,
    canManageOperators: false,
    canProcessLGPD: false,
  },
  SUPERVISOR: {
    canViewMetrics: true,
    canViewConversations: true,
    canViewFullPII: true,
    canSearchCustomers: true,
    canViewAuditLogs: true,
    canExportData: false,
    canManageOperators: false,
    canProcessLGPD: false,
  },
  ADMIN: {
    canViewMetrics: true,
    canViewConversations: true,
    canViewFullPII: true,
    canSearchCustomers: true,
    canViewAuditLogs: true,
    canExportData: true,
    canManageOperators: true,
    canProcessLGPD: false,
  },
  COMPLIANCE: {
    canViewMetrics: true,
    canViewConversations: true,
    canViewFullPII: true,
    canSearchCustomers: true,
    canViewAuditLogs: true,
    canExportData: true,
    canManageOperators: false,
    canProcessLGPD: true,
  },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create a date relative to today
 * @param daysOffset - Number of days from today (negative for past)
 * @param hours - Hour of day (0-23)
 * @param minutes - Minutes (0-59)
 */
export function getRelativeDate(daysOffset: number, hours: number, minutes: number = 0): Date {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  date.setHours(hours, minutes, 0, 0);
  return date;
}
